/**
 * オフィスの 3D レイアウトを world.json の定義から自動生成する。
 *
 * 【設計方針】
 * NetworkWorld.Room は id / name / floor / lockedUntilFlag しか持たない（型は確定済みで変更不可）。
 * そのため 3D 上の寸法は world.json の任意フィールド `layout` から読み、
 * 無ければ「フロアごとに廊下を 1 本引き、その両側に部屋を並べる」規則で自動生成する。
 * データ担当が layout を書けばそれが優先され、書かなくてもゲームは成立する。
 *
 * 座標系: X = 東, Z = 南, Y = 上。フロア f の床は y = (f - 1) * FLOOR_SPACING。
 */

import type { NetworkWorld, RoomId } from '../types/network';

export const WALL_THICKNESS = 0.3;
export const WALL_HEIGHT = 3.2;
export const FLOOR_SPACING = 3.6;
export const CORRIDOR_HALF_DEPTH = 2.4;
export const DOOR_WIDTH = 2.0;
export const STAIR_LENGTH = 9;
export const DEFAULT_ROOM_WIDTH = 13;
export const DEFAULT_ROOM_DEPTH = 10;

export type FloorMaterial = 'carpet' | 'tile';

/** world.json の `layout` に書ける任意フィールド（省略可） */
export interface RoomLayoutSpec {
  width?: number;
  depth?: number;
  /** 廊下のどちら側に置くか */
  side?: 'north' | 'south';
  /** 同じ側での並び順（小さいほど西） */
  order?: number;
  material?: FloorMaterial;
}

export interface WorldLayoutSpec {
  rooms?: Record<RoomId, RoomLayoutSpec>;
  spawn?: {
    roomId: RoomId;
    /** 部屋の中心を原点とするローカル座標 */
    position?: [number, number, number];
    /** 初期の向き（ラジアン。0 = -Z 方向を向く） */
    yaw?: number;
  };
}

/** world.json 全体（NetworkWorld ＋ 3D レイアウト） */
export interface WorldData extends NetworkWorld {
  layout?: WorldLayoutSpec;
}

// ---------------------------------------------------------------

/** 歩ける矩形領域（XZ 平面）。プレイヤーはこの和集合の中しか移動できない */
export interface WalkRect {
  id: string;
  kind: 'room' | 'corridor' | 'doorway' | 'stair';
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  /** この領域の床の高さ */
  y: number;
  /** 階段の場合、x0 端と x1 端の高さ（それ以外は y と同じ） */
  yAtX0: number;
  yAtX1: number;
  roomId?: RoomId;
  material: FloorMaterial;
  floor: number;
}

/**
 * プレイヤーが「いまどこに居るか」。
 * 部屋の中なら roomId が入るが、廊下・階段では null になるため、
 * 階数（floor）と領域の種類（kind）を必ず添えて持ち回る。
 */
export interface PlayerPlace {
  roomId: RoomId | null;
  kind: WalkRect['kind'];
  floor: number;
}

export function placeFromRect(rect: WalkRect | null): PlayerPlace | null {
  if (!rect) return null;
  return { roomId: rect.roomId ?? null, kind: rect.kind, floor: rect.floor };
}

/** 2 つの現在地が同じ場所を指しているか（HUD の更新要否の判定に使う） */
export function samePlace(a: PlayerPlace | null, b: PlayerPlace | null): boolean {
  if (a === null || b === null) return a === b;
  return a.roomId === b.roomId && a.kind === b.kind && a.floor === b.floor;
}

/**
 * HUD の現在地ラベルの文言を作る。
 *
 * 部屋の中なら「1F 営業部」。
 * 廊下・階段には部屋が無いので、以前は空文字になり
 * 「自分が何階にいるか分からなくなる」不具合になっていた。
 * 部屋名が無くても階数だけは必ず出す。
 */
export function formatPlaceLabel(place: PlayerPlace | null, roomName: string | null): string {
  if (!place) return '';
  const floor = `${place.floor}F`;
  if (place.roomId) return roomName ? `${floor} ${roomName}` : floor;
  switch (place.kind) {
    case 'corridor':
      return `${floor} 廊下`;
    case 'stair':
      return `${floor} 階段`;
    case 'doorway':
      return `${floor} 出入口`;
    default:
      return floor;
  }
}

export interface WallSegment {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  y: number;
  height: number;
}

export interface RoomLayout {
  id: RoomId;
  name: string;
  floor: number;
  /** 部屋の中心（3D 世界座標） */
  center: [number, number, number];
  width: number;
  depth: number;
  y: number;
  material: FloorMaterial;
  rect: WalkRect;
  /** ドアの中心位置（世界座標） */
  doorPosition: [number, number, number];
  lockedUntilFlag?: string;
}

export interface OfficeLayout {
  rooms: Record<RoomId, RoomLayout>;
  walkRects: WalkRect[];
  walls: WallSegment[];
  floors: WalkRect[];
  spawn: { position: [number, number, number]; yaw: number };
  floorNumbers: number[];
}

function floorY(floor: number): number {
  return (floor - 1) * FLOOR_SPACING;
}

/** 部屋のローカル座標（部屋の中心が原点）を世界座標に変換する */
export function roomLocalToWorld(
  room: RoomLayout,
  local: [number, number, number],
): [number, number, number] {
  return [room.center[0] + local[0], room.y + local[1], room.center[2] + local[2]];
}

/**
 * オフィス全体のレイアウトを組み立てる。
 */
export function buildOfficeLayout(world: WorldData): OfficeLayout {
  const spec = world.layout ?? {};
  const roomSpecs = spec.rooms ?? {};

  const roomIds = Object.keys(world.rooms);
  const floors = [...new Set(roomIds.map((id) => world.rooms[id].floor))].sort((a, b) => a - b);

  const rooms: Record<RoomId, RoomLayout> = {};
  const walkRects: WalkRect[] = [];
  const walls: WallSegment[] = [];
  /** フロアごとの「ドアで壁を抜く位置」と、部屋を並べ終えた廊下の長さ */
  const floorInfo = new Map<
    number,
    { doorGaps: { north: [number, number][]; south: [number, number][] }; length: number }
  >();

  for (const floor of floors) {
    const onFloor = roomIds
      .filter((id) => world.rooms[id].floor === floor)
      .sort((a, b) => {
        const oa = roomSpecs[a]?.order ?? roomIds.indexOf(a);
        const ob = roomSpecs[b]?.order ?? roomIds.indexOf(b);
        return oa - ob;
      });

    // 明示指定が無ければ north / south を交互に振る
    const sides = new Map<RoomId, 'north' | 'south'>();
    let autoIndex = 0;
    for (const id of onFloor) {
      const explicit = roomSpecs[id]?.side;
      sides.set(id, explicit ?? (autoIndex % 2 === 0 ? 'north' : 'south'));
      autoIndex++;
    }

    const cursor = { north: 2, south: 2 };
    const doorGaps: { north: [number, number][]; south: [number, number][] } = {
      north: [],
      south: [],
    };

    for (const id of onFloor) {
      const room = world.rooms[id];
      const rs = roomSpecs[id] ?? {};
      const width = rs.width ?? DEFAULT_ROOM_WIDTH;
      const depth = rs.depth ?? DEFAULT_ROOM_DEPTH;
      const side = sides.get(id) as 'north' | 'south';
      const material: FloorMaterial = rs.material ?? (room.floor === 1 ? 'tile' : 'carpet');

      const x0 = cursor[side];
      const x1 = x0 + width;
      cursor[side] = x1 + 2; // 部屋の間に少し壁を挟む

      const inner = CORRIDOR_HALF_DEPTH + WALL_THICKNESS;
      const z0 = side === 'north' ? -(inner + depth) : inner;
      const z1 = side === 'north' ? -inner : inner + depth;

      const y = floorY(room.floor);
      const rect: WalkRect = {
        id: `room:${id}`,
        kind: 'room',
        x0,
        z0,
        x1,
        z1,
        y,
        yAtX0: y,
        yAtX1: y,
        roomId: id,
        material,
        floor: room.floor,
      };
      walkRects.push(rect);

      const centerX = (x0 + x1) / 2;
      const doorZ = side === 'north' ? -CORRIDOR_HALF_DEPTH : CORRIDOR_HALF_DEPTH;
      doorGaps[side].push([centerX - DOOR_WIDTH / 2, centerX + DOOR_WIDTH / 2]);

      // 部屋と廊下をつなぐ「敷居」領域（壁の厚み分）
      walkRects.push({
        id: `door:${id}`,
        kind: 'doorway',
        x0: centerX - DOOR_WIDTH / 2,
        x1: centerX + DOOR_WIDTH / 2,
        z0: side === 'north' ? -inner : CORRIDOR_HALF_DEPTH,
        z1: side === 'north' ? -CORRIDOR_HALF_DEPTH : inner,
        y,
        yAtX0: y,
        yAtX1: y,
        roomId: id,
        material,
        floor: room.floor,
      });

      // 部屋の外壁 3 面（廊下側は廊下の壁で兼ねる）
      const far = side === 'north' ? z0 : z1;
      walls.push({ x0, z0: far, x1, z1: far, y, height: WALL_HEIGHT });
      walls.push({ x0, z0, x1: x0, z1, y, height: WALL_HEIGHT });
      walls.push({ x0: x1, z0, x1, z1, y, height: WALL_HEIGHT });

      const layout: RoomLayout = {
        id,
        name: room.name,
        floor: room.floor,
        center: [centerX, y, (z0 + z1) / 2],
        width,
        depth,
        y,
        material,
        rect,
        doorPosition: [centerX, y, doorZ],
      };
      if (room.lockedUntilFlag) layout.lockedUntilFlag = room.lockedUntilFlag;
      rooms[id] = layout;
    }

    floorInfo.set(floor, { doorGaps, length: Math.max(cursor.north, cursor.south, 16) });
  }

  // 廊下は全フロアで同じ長さにそろえる。
  // こうしないと上の階の廊下が下の階の階段の真上に張り出し、
  // 「廊下の床が階段にかぶさって降りられない」状態になる。
  const corridorEnd = Math.max(...[...floorInfo.values()].map((f) => f.length), 16);

  for (const floor of floors) {
    const y = floorY(floor);
    const doorGaps = floorInfo.get(floor)!.doorGaps;

    walkRects.push({
      id: `corridor:${floor}`,
      kind: 'corridor',
      x0: 0,
      z0: -CORRIDOR_HALF_DEPTH,
      x1: corridorEnd,
      z1: CORRIDOR_HALF_DEPTH,
      y,
      yAtX0: y,
      yAtX1: y,
      material: 'tile',
      floor,
    });

    // 廊下の側壁（ドアの位置だけ穴を空ける）
    for (const side of ['north', 'south'] as const) {
      const z = side === 'north' ? -CORRIDOR_HALF_DEPTH : CORRIDOR_HALF_DEPTH;
      const gaps = [...doorGaps[side]].sort((a, b) => a[0] - b[0]);
      let x = 0;
      for (const [gx0, gx1] of gaps) {
        if (gx0 > x) walls.push({ x0: x, z0: z, x1: gx0, z1: z, y, height: WALL_HEIGHT });
        x = Math.max(x, gx1);
      }
      if (x < corridorEnd) {
        walls.push({ x0: x, z0: z, x1: corridorEnd, z1: z, y, height: WALL_HEIGHT });
      }
    }
    // 廊下の西端
    walls.push({
      x0: 0,
      z0: -CORRIDOR_HALF_DEPTH,
      x1: 0,
      z1: CORRIDOR_HALF_DEPTH,
      y,
      height: WALL_HEIGHT,
    });
  }

  buildStairwell(floors, corridorEnd, walkRects, walls);

  const spawn = resolveSpawn(spec, rooms, walkRects);

  return {
    rooms,
    walkRects,
    walls,
    floors: walkRects,
    spawn,
    floorNumbers: floors,
  };
}

/**
 * 廊下の東端に階段室を 1 本立てる。
 *
 * 階段室は廊下と同じ幅を南北 2 レーンに分けて使う。
 *   北レーン: 上りのスロープ（フロア f の床から f+1 の床まで、東へ向かって上る）
 *   南レーン: 各フロアの踊り場（水平）。廊下の東端とスロープの上端をつなぐ
 *
 * こうすると、あるフロアの歩行領域が別のフロアのスロープに覆いかぶさらない。
 * 上りは「廊下を東へ → 北レーンを上る → 上階の踊り場へ北から南へ抜ける」、
 * 下りはその逆。踊り場とスロープの高さが揃うのは東端と西端なので、
 * 行き来できるのもその 2 か所だけになる（途中は手すりで塞がる）。
 */
function buildStairwell(
  floors: number[],
  corridorEnd: number,
  walkRects: WalkRect[],
  walls: WallSegment[],
): void {
  if (floors.length === 0) return;
  const x0 = corridorEnd;
  const x1 = corridorEnd + STAIR_LENGTH;

  for (const floor of floors) {
    const y = floorY(floor);

    // 南レーン：このフロアの踊り場（廊下の東端と地続き）
    walkRects.push({
      id: `landing:${floor}`,
      kind: 'stair',
      x0,
      z0: 0,
      x1,
      z1: CORRIDOR_HALF_DEPTH,
      y,
      yAtX0: y,
      yAtX1: y,
      material: 'tile',
      floor,
    });

    // 北レーン：上階へのスロープ
    if (floors.includes(floor + 1)) {
      walkRects.push({
        id: `stair:${floor}`,
        kind: 'stair',
        x0,
        z0: -CORRIDOR_HALF_DEPTH,
        x1,
        z1: 0,
        y,
        yAtX0: y,
        yAtX1: floorY(floor + 1),
        material: 'tile',
        floor,
      });
    } else {
      // 最上階には上りが無いので、北レーンの入口を壁で塞ぐ
      walls.push({ x0, z0: -CORRIDOR_HALF_DEPTH, x1: x0, z1: 0, y, height: WALL_HEIGHT });
    }

    // 階段室の外壁（南北と東端）
    walls.push({ x0, z0: -CORRIDOR_HALF_DEPTH, x1, z1: -CORRIDOR_HALF_DEPTH, y, height: FLOOR_SPACING });
    walls.push({ x0, z0: CORRIDOR_HALF_DEPTH, x1, z1: CORRIDOR_HALF_DEPTH, y, height: FLOOR_SPACING });
    walls.push({ x0: x1, z0: -CORRIDOR_HALF_DEPTH, x1, z1: CORRIDOR_HALF_DEPTH, y, height: FLOOR_SPACING });

    // 手すり（スロープと踊り場の境目。両端は乗り移れるように空けておく）
    walls.push({ x0: x0 + 1.6, z0: 0, x1: x1 - 1.6, z1: 0, y, height: 1.0 });
  }
}

function resolveSpawn(
  spec: WorldLayoutSpec,
  rooms: Record<RoomId, RoomLayout>,
  rects: WalkRect[],
): { position: [number, number, number]; yaw: number } {
  const ids = Object.keys(rooms);
  const targetId = spec.spawn?.roomId && rooms[spec.spawn.roomId] ? spec.spawn.roomId : ids[0];
  const room = targetId ? rooms[targetId] : undefined;
  if (!room) {
    const first = rects[0];
    return {
      position: first ? [(first.x0 + first.x1) / 2, first.y, (first.z0 + first.z1) / 2] : [0, 0, 0],
      yaw: 0,
    };
  }
  const local = spec.spawn?.position ?? [0, 0, 0];
  return {
    position: roomLocalToWorld(room, local),
    yaw: spec.spawn?.yaw ?? 0,
  };
}

// ---------------------------------------------------------------
// 当たり判定
// ---------------------------------------------------------------

/** 点 (x, z) を含む歩行領域を返す。無ければ null */
export function rectAt(layout: OfficeLayout, x: number, z: number, y: number): WalkRect | null {
  let best: WalkRect | null = null;
  let bestDy = Infinity;
  for (const rect of layout.walkRects) {
    if (x < rect.x0 || x > rect.x1 || z < rect.z0 || z > rect.z1) continue;
    const rectY = heightAt(rect, x);
    const dy = Math.abs(rectY - y);
    // 同じ XZ に複数フロアが重なるため、いま居る高さに一番近いものを選ぶ
    if (dy < bestDy) {
      bestDy = dy;
      best = rect;
    }
  }
  return best;
}

/** 領域内の床の高さ（階段は傾斜する） */
export function heightAt(rect: WalkRect, x: number): number {
  if (rect.yAtX0 === rect.yAtX1) return rect.y;
  const t = Math.min(1, Math.max(0, (x - rect.x0) / (rect.x1 - rect.x0)));
  return rect.yAtX0 + (rect.yAtX1 - rect.yAtX0) * t;
}

/**
 * 半径 radius の円が (x, z) に収まるか。
 * 中心と 4 方向の縁を歩行領域に照らして判定する（壁抜け防止）。
 */
export function canStandAt(
  layout: OfficeLayout,
  x: number,
  z: number,
  y: number,
  radius: number,
): { ok: boolean; y: number } {
  const center = rectAt(layout, x, z, y);
  if (!center) return { ok: false, y };

  const probes: [number, number][] = [
    [x + radius, z],
    [x - radius, z],
    [x, z + radius],
    [x, z - radius],
  ];
  for (const [px, pz] of probes) {
    const rect = rectAt(layout, px, pz, y);
    if (!rect) return { ok: false, y };
    // 段差が大きい領域へは踏み出せない（フロアをまたぐ落下の防止）
    if (Math.abs(heightAt(rect, px) - heightAt(center, x)) > 0.6) return { ok: false, y };
  }
  return { ok: true, y: heightAt(center, x) };
}
